(() => {
  const templateSelector = 'template[data-theme-cart-complementary-template]';
  const recommendationIdentity = (template) => template && JSON.stringify([
    [...new Set((template.dataset.themeCartProductIds || '').split(',').filter(Boolean))].sort(),
    template.dataset.themeRecommendationCurrency, template.dataset.themeRecommendationThemeId,
    template.dataset.themeRecommendationLimit, template.dataset.themeRecommendationSurface,
    template.dataset.themeRecommendationOwnerId
  ]);
  const nodeKey = (node) => {
    if (node.nodeType !== 1) return null;
    // QuantityInput caches its input. Its Shopify line-derived ID may change
    // when another row is removed; preserve the live control within that owner.
    if (node.matches('quantity-input input')) return 'quantity-component-input';
    if (node.hasAttribute('data-theme-item-key')) return `item:${node.getAttribute('data-theme-item-key')}`;
    if (node.matches(templateSelector)) return 'cart-recommendation-template';
    return node.id ? `id:${node.id}` : null;
  };
  const compatible = (current, source) => current.nodeType === source.nodeType &&
    current.nodeName === source.nodeName && nodeKey(current) === nodeKey(source) &&
    (current.nodeType !== 1 || current.classList[0] === source.classList[0]);

  // Reconcile confirmed Shopify markup while retaining cart controls and owned
  // recommendation islands. No quantity, inventory or price is inferred here.
  window.ThemeCartRender = {
    announce(region, { title, quantity }) {
      if (!region || !title || !Number.isSafeInteger(quantity) || quantity < 0) return;
      const { quantityLabel, removalMessage, subtotalLabel, subtotal } = region.dataset;
      if (!quantityLabel || !removalMessage || !subtotalLabel || !subtotal) return;
      region.textContent = `${title}. ${quantity === 0 ? removalMessage : `${quantityLabel}: ${quantity}`}. ${subtotalLabel}: ${subtotal}.`;
    },
    reconcile(current, source) {
      const currentTemplate = current.querySelector(templateSelector);
      const sourceTemplate = source.querySelector(templateSelector);
      const preserveRecommendations = Boolean(currentTemplate && sourceTemplate &&
        recommendationIdentity(currentTemplate) === recommendationIdentity(sourceTemplate));
      const preserved = new Set(preserveRecommendations
        ? [currentTemplate, ...current.querySelectorAll('.theme-cart-complementary')]
        : []);
      const reconcileNode = (target, next) => {
        if (preserved.has(target)) return;
        if (target.nodeType !== 1) {
          if (target.nodeValue !== next.nodeValue) target.nodeValue = next.nodeValue;
          return;
        }
        // These mounts own runtime markup, separate from Shopify's section HTML.
        if (target.matches('[data-theme-free-shipping-mount]')) return;
        for (const attribute of [...target.attributes]) {
          if (!next.hasAttribute(attribute.name) &&
              !/^data-theme-.*(?:bound|session-id|device-category|pointer-focus)$/.test(attribute.name) &&
              !(attribute.name === 'style' && target.matches('.theme-main-cart__summary'))) {
            target.removeAttribute(attribute.name);
          }
        }
        for (const attribute of [...next.attributes]) {
          if (/^data-theme-.*(?:session-id|device-category)$/.test(attribute.name)) continue;
          let value = attribute.value;
          if (attribute.name === 'class' && target.classList.contains('theme-main-cart__summary--sticky')) {
            value += ' theme-main-cart__summary--sticky';
          }
          if (target.getAttribute(attribute.name) !== value) target.setAttribute(attribute.name, value);
        }
        // The controller owns live announcements; Shopify supplies only their
        // localized labels and confirmed subtotal. Preserve the stable region.
        if (target.matches('[data-theme-cart-status]')) return;
        if (target.matches('input') && target.value !== next.value) target.value = next.value;
        if (target.matches('template')) {
          // Membership changed: a fresh template must get a fresh binding.
          target.replaceWith(next.cloneNode(true));
          return;
        }
        const previous = [...target.childNodes].filter((node) => !preserved.has(node));
        const used = new Set();
        let cursor = target.firstChild;
        for (const child of next.childNodes) {
          if (preserveRecommendations && child.nodeType === 1 && child.matches(templateSelector)) continue;
          while (cursor && preserved.has(cursor)) cursor = cursor.nextSibling;
          const key = nodeKey(child);
          const match = child.nodeType === 1 && child.matches(templateSelector) ? null : previous.find((node) => !used.has(node) &&
            (key ? nodeKey(node) === key && node.nodeName === child.nodeName : compatible(node, child)));
          const node = match || child.cloneNode(true);
          if (match) used.add(match);
          if (node !== cursor) target.insertBefore(node, cursor);
          if (match) reconcileNode(node, child);
          cursor = node.parentNode === target ? node.nextSibling : cursor;
        }
        for (const node of previous) if (!used.has(node) && node.parentNode === target) node.remove();
      };
      reconcileNode(current, source);
      return { preserveRecommendations };
    }
  };
})();
